/**
 * Crops a video cover before it is uploaded.
 *
 * The whole picture is shown with a crop box over it, which is dragged to move
 * it and resized from its corners. Given an `aspect` — the video's shape — the
 * box keeps that shape, because a cover of any other shape is cropped again by
 * Instagram and Facebook and no longer looks as it was cropped here. Without
 * one (no video yet) it is a free crop. The selected area is exported as a JPEG
 * at its own resolution (capped at 1920 px on the long side), so nothing is
 * stretched.
 *
 * Written here rather than pulled in as a dependency: it needs only a box with
 * move and resize, and the same small component exists in the corp dashboard
 * (components/cover-crop-dialog.tsx).
 */
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

interface Props {
  isOpen: boolean;
  /** An object URL or same-origin URL the canvas may read. */
  src: string | null;
  onCancel: () => void;
  onDone: (blob: Blob) => void;
  busy?: boolean;
  aspect?: number | null;
}

/** The picture is shown fitted inside this box. */
const MAX_VIEW_W = 460;
const MAX_VIEW_H = 420;
/** Smallest crop box, in screen pixels, so a corner can always be grabbed. */
const MIN_BOX = 40;
/** Longest side of the exported cover. */
const MAX_OUTPUT = 1920;

type Box = { x: number; y: number; w: number; h: number };
type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se';

/** The largest box of `aspect` that fits the view, centred — or the whole view when the crop is free. */
function initialBox(viewW: number, viewH: number, aspect?: number | null): Box {
  if (!aspect) return { x: 0, y: 0, w: viewW, h: viewH };
  const w = Math.min(viewW, viewH * aspect);
  const h = w / aspect;
  return { x: (viewW - w) / 2, y: (viewH - h) / 2, w, h };
}

/**
 * A corner drag with the shape held: the opposite corner stays put, the larger
 * of the two pointer movements sets the size, and the box stops at the picture's
 * edge and at MIN_BOX on its shorter side.
 */
function resizeLocked(b: Box, handle: Handle, dx: number, dy: number, aspect: number, viewW: number, viewH: number): Box {
  const east = handle.includes('e');
  const south = handle.includes('s');
  const anchorX = east ? b.x : b.x + b.w;
  const anchorY = south ? b.y : b.y + b.h;
  const fromX = b.w + (east ? dx : -dx);
  const fromY = (b.h + (south ? dy : -dy)) * aspect;
  const wanted = Math.abs(dx) >= Math.abs(dy * aspect) ? fromX : fromY;
  const maxW = Math.min(east ? viewW - anchorX : anchorX, (south ? viewH - anchorY : anchorY) * aspect);
  const minW = Math.max(MIN_BOX, MIN_BOX * aspect);
  const w = Math.min(maxW, Math.max(minW, wanted));
  const h = w / aspect;
  return { x: east ? anchorX : anchorX - w, y: south ? anchorY : anchorY - h, w, h };
}

export function CoverCropDialog({ isOpen, src, onCancel, onDone, busy, aspect }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [box, setBox] = useState<Box>({ x: 0, y: 0, w: 0, h: 0 });
  const drag = useRef<{ handle: Handle; startX: number; startY: number; box: Box } | null>(null);

  // The picture fitted inside the view, and its on-screen size.
  const scale = natural ? Math.min(MAX_VIEW_W / natural.w, MAX_VIEW_H / natural.h) : 1;
  const viewW = natural ? Math.round(natural.w * scale) : MAX_VIEW_W;
  const viewH = natural ? Math.round(natural.h * scale) : MAX_VIEW_H;

  useEffect(() => {
    setNatural(null);
  }, [src]);

  // A new picture starts with as much of it selected as the shape allows.
  useEffect(() => {
    if (natural) setBox(initialBox(viewW, viewH, aspect));
  }, [natural, viewW, viewH, aspect]);

  const start = (handle: Handle) => (e: PointerEvent<HTMLElement>) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { handle, startX: e.clientX, startY: e.clientY, box };
  };

  const move = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const b = d.box;

    if (d.handle === 'move') {
      setBox({
        ...b,
        x: Math.min(viewW - b.w, Math.max(0, b.x + dx)),
        y: Math.min(viewH - b.h, Math.max(0, b.y + dy)),
      });
      return;
    }

    if (aspect) {
      setBox(resizeLocked(b, d.handle, dx, dy, aspect, viewW, viewH));
      return;
    }

    // Resize from a corner: the opposite corner stays put.
    let left = b.x;
    let top = b.y;
    let right = b.x + b.w;
    let bottom = b.y + b.h;
    if (d.handle.includes('w')) left = Math.min(right - MIN_BOX, Math.max(0, b.x + dx));
    if (d.handle.includes('e')) right = Math.max(left + MIN_BOX, Math.min(viewW, b.x + b.w + dx));
    if (d.handle.includes('n')) top = Math.min(bottom - MIN_BOX, Math.max(0, b.y + dy));
    if (d.handle.includes('s')) bottom = Math.max(top + MIN_BOX, Math.min(viewH, b.y + b.h + dy));
    setBox({ x: left, y: top, w: right - left, h: bottom - top });
  };

  const end = () => {
    drag.current = null;
  };

  const finish = () => {
    const img = imgRef.current;
    if (!img || !natural) return;
    // The selected area in the original picture's pixels.
    const sx = box.x / scale;
    const sy = box.y / scale;
    const sw = box.w / scale;
    const sh = box.h / scale;
    const shrink = Math.min(1, MAX_OUTPUT / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * shrink);
    canvas.height = Math.round(sh * shrink);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) onDone(blob); }, 'image/jpeg', 0.92);
  };

  const corner = 'absolute h-3.5 w-3.5 rounded-sm border-2 border-white bg-blue-600 shadow';

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Crop the thumbnail" size="md">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Drag the box to move it, and drag a corner to resize it
          {aspect ? ' — it keeps your video\'s shape, so the networks show it as cropped' : ''}. The area inside
          the box becomes the thumbnail.
        </p>
        <div
          className="relative mx-auto touch-none select-none overflow-hidden rounded-md bg-slate-900"
          style={{ width: viewW, height: viewH }}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              className="absolute inset-0 h-full w-full"
            />
          )}
          {natural && (
            <div
              className="absolute cursor-move border-2 border-white"
              style={{
                left: box.x,
                top: box.y,
                width: box.w,
                height: box.h,
                // Dims everything outside the box.
                boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
              }}
              onPointerDown={start('move')}
            >
              <span className={`${corner} -left-2 -top-2 cursor-nwse-resize`} onPointerDown={start('nw')} />
              <span className={`${corner} -right-2 -top-2 cursor-nesw-resize`} onPointerDown={start('ne')} />
              <span className={`${corner} -bottom-2 -left-2 cursor-nesw-resize`} onPointerDown={start('sw')} />
              <span className={`${corner} -bottom-2 -right-2 cursor-nwse-resize`} onPointerDown={start('se')} />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={finish} disabled={!natural || busy} loading={busy}>Use this crop</Button>
        </div>
      </div>
    </Modal>
  );
}
